import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuthStore } from '../store/auth';
import type { Project, Invoice, Offer, TimeEntry } from '../types';

const projectStatusLabels: Record<string, string> = {
  INQUIRY: 'Anfrage', OFFER_SENT: 'Angebot', ORDER: 'Auftrag',
  IN_PROGRESS: 'In Arbeit', DONE: 'Fertig', ARCHIVED: 'Archiv',
};

const projectStatusStyles: Record<string, string> = {
  INQUIRY:     'bg-slate-100 text-slate-600',
  OFFER_SENT:  'bg-amber-50 text-amber-700 border border-amber-200',
  ORDER:       'bg-blue-50 text-blue-700 border border-blue-200',
  IN_PROGRESS: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  DONE:        'bg-green-50 text-green-600',
  ARCHIVED:    'bg-slate-100 text-slate-400',
};

const invoiceStatusLabels: Record<string, string> = {
  DRAFT: 'Entwurf', SENT: 'Gesendet', PAID: 'Bezahlt', OVERDUE: 'Überfällig', CANCELLED: 'Storniert',
};

const invoiceStatusStyles: Record<string, string> = {
  DRAFT:     'bg-slate-100 text-slate-600',
  SENT:      'bg-blue-50 text-blue-700 border border-blue-200',
  PAID:      'bg-emerald-50 text-emerald-700 border border-emerald-200',
  OVERDUE:   'bg-red-50 text-red-600 border border-red-200',
  CANCELLED: 'bg-slate-100 text-slate-400',
};

/** Calculate hours worked this week from time entries */
function getWeeklyHours(entries: TimeEntry[]): number {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);

  let totalMinutes = 0;
  for (const entry of entries) {
    const start = new Date(entry.startedAt);
    if (start < monday) continue;
    const end = entry.endedAt ? new Date(entry.endedAt) : now;
    const diffMs = end.getTime() - start.getTime();
    const workMinutes = diffMs / 60000 - (entry.breakMin || 0);
    if (workMinutes > 0) totalMinutes += workMinutes;
  }
  return Math.round((totalMinutes / 60) * 10) / 10;
}

/** Format currency as German locale */
function fmtCurrency(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
}

/** Format date as German short format */
function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  const { data: projects = [], isLoading: loadingProjects } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const { data: invoices = [], isLoading: loadingInvoices } = useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => api.get('/invoices').then((r) => r.data),
  });

  const { data: offers = [], isLoading: loadingOffers } = useQuery<Offer[]>({
    queryKey: ['offers'],
    queryFn: () => api.get('/offers').then((r) => r.data),
  });

  const { data: timeEntries = [], isLoading: loadingTime } = useQuery<TimeEntry[]>({
    queryKey: ['time'],
    queryFn: () => api.get('/time').then((r) => r.data),
  });

  const isLoading = loadingProjects || loadingInvoices || loadingOffers || loadingTime;

  // Derived stats
  const activeProjects = projects.filter((p) => p.status !== 'ARCHIVED' && p.status !== 'DONE');
  const openOffers = offers.filter((o) => o.status === 'DRAFT' || o.status === 'SENT');
  const unpaidInvoices = invoices.filter((i) => !i.paidAt);
  const weeklyHours = getWeeklyHours(timeEntries);

  // Recent items
  const recentProjects = [...activeProjects].slice(0, 5);
  const recentInvoices = [...invoices].slice(0, 5);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Guten Morgen';
    if (h < 18) return 'Guten Tag';
    return 'Guten Abend';
  })();

  return (
    <div>
      {/* Hero greeting */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-brand-500 to-purple-500 p-8 mb-8 animate-fade-in">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-white/5 rounded-full translate-y-1/2" />
        </div>
        <div className="relative z-10">
          <p className="text-brand-200 text-sm font-medium mb-1">{greeting}</p>
          <h1 className="text-2xl font-bold text-white">{user?.name}</h1>
          <p className="text-white/60 text-sm mt-1">Hier ist Ihre aktuelle Übersicht</p>
        </div>
      </div>

      {/* Quick action buttons */}
      <div className="flex flex-wrap gap-3 mb-8 animate-fade-in" style={{ animationDelay: '80ms' }}>
        <Link to="/projects" state={{ create: true }} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Neues Projekt
        </Link>
        <Link to="/offers" state={{ create: true }} className="sp-btn-secondary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Neues Angebot
        </Link>
        <Link to="/invoices" state={{ create: true }} className="sp-btn-secondary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Neue Rechnung
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 stagger">
        <StatCard
          label="Aktive Projekte"
          value={activeProjects.length}
          to="/projects"
          gradient="from-blue-500 to-blue-600"
          lightBg="bg-blue-50"
          loading={isLoading}
          icon={<path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />}
        />
        <StatCard
          label="Offene Angebote"
          value={openOffers.length}
          to="/offers"
          gradient="from-amber-500 to-orange-500"
          lightBg="bg-amber-50"
          loading={isLoading}
          icon={<path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
        />
        <StatCard
          label="Unbezahlte Rechnungen"
          value={unpaidInvoices.length}
          to="/invoices"
          gradient="from-rose-500 to-pink-500"
          lightBg="bg-rose-50"
          loading={isLoading}
          icon={<path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
        />
        <StatCard
          label="Stunden diese Woche"
          value={weeklyHours + ' h'}
          to="/time"
          gradient="from-emerald-500 to-teal-500"
          lightBg="bg-emerald-50"
          loading={isLoading}
          isText
          icon={<path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />}
        />
      </div>

      {/* Two-column layout: Recent Projects + Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent projects */}
        <div className="sp-card overflow-hidden animate-scale-in" style={{ animationDelay: '200ms' }}>
          <div className="px-6 py-5 flex items-center justify-between border-b border-surface-100">
            <div>
              <h2 className="sp-section-title">Letzte Projekte</h2>
              <p className="text-xs text-slate-400 mt-0.5">{activeProjects.length} aktive Projekte</p>
            </div>
            <Link to="/projects" className="text-sm text-brand-500 font-semibold hover:text-brand-600 transition-colors flex items-center gap-1">
              Alle anzeigen
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {loadingProjects ? (
            <div className="flex items-center justify-center py-12">
              <svg className="sp-spinner text-brand-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : recentProjects.length === 0 ? (
            <div className="sp-empty">
              <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <span className="text-sm font-medium">Keine aktiven Projekte</span>
              <span className="text-xs mt-1">Erstellen Sie Ihr erstes Projekt</span>
            </div>
          ) : (
            <div className="divide-y divide-surface-100/80">
              {recentProjects.map((p) => (
                <Link
                  key={p.id}
                  to="/projects"
                  className="flex items-center justify-between px-6 py-4 hover:bg-brand-50/30 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{p.customer?.name || '-'}</p>
                  </div>
                  <span className={`sp-badge ml-3 flex-shrink-0 ${projectStatusStyles[p.status] || 'bg-slate-100 text-slate-600'}`}>
                    {projectStatusLabels[p.status] || p.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent invoices */}
        <div className="sp-card overflow-hidden animate-scale-in" style={{ animationDelay: '300ms' }}>
          <div className="px-6 py-5 flex items-center justify-between border-b border-surface-100">
            <div>
              <h2 className="sp-section-title">Letzte Rechnungen</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {unpaidInvoices.length} offen &middot; {fmtCurrency(unpaidInvoices.reduce((s, i) => s + Number(i.totalGross), 0))}
              </p>
            </div>
            <Link to="/invoices" className="text-sm text-brand-500 font-semibold hover:text-brand-600 transition-colors flex items-center gap-1">
              Alle anzeigen
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {loadingInvoices ? (
            <div className="flex items-center justify-center py-12">
              <svg className="sp-spinner text-brand-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : recentInvoices.length === 0 ? (
            <div className="sp-empty">
              <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-sm font-medium">Keine Rechnungen vorhanden</span>
              <span className="text-xs mt-1">Erstellen Sie Ihre erste Rechnung</span>
            </div>
          ) : (
            <div className="divide-y divide-surface-100/80">
              {recentInvoices.map((inv) => {
                const isPaid = !!inv.paidAt;
                const isOverdue = !isPaid && inv.dueDate && new Date(inv.dueDate) < new Date();
                const displayStatus = isPaid ? 'PAID' : isOverdue ? 'OVERDUE' : inv.status;

                return (
                  <Link
                    key={inv.id}
                    to="/invoices"
                    className="flex items-center justify-between px-6 py-4 hover:bg-brand-50/30 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800">{inv.number}</p>
                        {inv.dueDate && (
                          <span className="text-2xs text-slate-400">
                            Fällig: {fmtDate(inv.dueDate)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {inv.project?.title || '-'}
                        {inv.project?.customer?.name ? ` \u2013 ${inv.project.customer.name}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                      <span className="text-sm font-semibold text-slate-700">
                        {fmtCurrency(Number(inv.totalGross))}
                      </span>
                      <span className={`sp-badge ${invoiceStatusStyles[displayStatus] || 'bg-slate-100 text-slate-600'}`}>
                        {isPaid && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {invoiceStatusLabels[displayStatus] || displayStatus}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Stat Card component ──────────────────────────────── */

function StatCard({ label, value, to, icon, gradient, lightBg, isText, loading }: {
  label: string; value: number | string; to: string;
  icon: React.ReactNode; gradient: string; lightBg: string;
  isText?: boolean; loading?: boolean;
}) {
  return (
    <Link to={to} className="sp-card-interactive p-5 group">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md`}>
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            {icon}
          </svg>
        </div>
        <svg className="w-4 h-4 text-slate-300 group-hover:text-brand-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
      {loading ? (
        <div className={`${isText ? 'h-7' : 'h-9'} flex items-center`}>
          <div className={`${lightBg} rounded-lg animate-pulse h-5 w-16`} />
        </div>
      ) : (
        <p className={`${isText ? 'text-lg' : 'text-3xl'} font-bold text-slate-900 tracking-tight`}>{value}</p>
      )}
      <p className="text-xs text-slate-400 font-medium mt-1">{label}</p>
    </Link>
  );
}
